import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useQuery } from 'react-apollo';

import { getAsset } from '../../../../../../../../../util/assets';
import PriceDisplay from '../../../../../../../../../components/global/PriceDisplay';
import Badge from '../../../../../../../../../components/global/Badge';
import { REQUIRES_PRINT_PACKAGE_ALERT_MODAL } from '../RequiresPrintPackageAlert';
import useOpenModal from '../../../../../../../../../hooks/useOpenModal';
import { PrintProductConfiguratorModal } from '../../../../../../../../../services/prints/components/PrintProductConfigurator';
import PrintProductSizeRoesConfiguratorModal from '../PrintProductsGroup/components/PrintProductSizeRoesConfigurator';
import { getPreferredPrintProductSizeOrientation } from '../PrintProductsGroup/helpers';
import CheckoutItemQuantityInput from '../../../../../../../../../services/checkout/components/CheckoutCartItem/CheckoutItemQuantityInput';
import { printProductSizeBaseInfo } from '../../../../../../../../../graphql/queries/print-product';

const propTypes = {
  className: PropTypes.string,
  photoPricingPrintProductItem: PropTypes.shape({
    id: PropTypes.string.isRequired,
    print_product: PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }).isRequired,
    print_product_size: PropTypes.shape({
      id: PropTypes.string.isRequired,
      print_size: PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
      }).isRequired,
    }).isRequired,
  }).isRequired,
  readOnly: PropTypes.bool,
  photo: PropTypes.shape({
    id: PropTypes.string,
  }),
  disabled: PropTypes.bool,
  disabledElement: PropTypes.node,
  cartItem: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  onCartItemToggle: PropTypes.func.isRequired,
  onCartItemUpdate: PropTypes.func.isRequired,
  albumMembershipGroup: PropTypes.shape({
    id: PropTypes.string,
  }),
  identity: PropTypes.shape({
    id: PropTypes.string,
  }),
  isAllPhotos: PropTypes.bool,
  printBackgrounds: PropTypes.array,
  watermarkSettings: PropTypes.shape({
    wallpaperCustomText: PropTypes.string,
    wallpaperFontSize: PropTypes.number,
  }),
};

const PrintProductIndividual = ({
  className,
  photo,
  photoPricingPrintProductItem,
  readOnly,
  disabled,
  disabledElement,
  cartItem,
  onCartItemToggle,
  onCartItemUpdate,
  albumMembershipGroup,
  identity,
  isAllPhotos,
  printBackgrounds,
  watermarkSettings,
}) => {
  const {
    print_product: printProduct = {},
    print_product_size: printProductSize = {},
  } = photoPricingPrintProductItem ?? {};

  const {
    data: { printProducts: printProductWithSizeInfo } = {},
    loading: loadingPrintProductSizeInfo,
  } = useQuery(printProductSizeBaseInfo, {
    variables: {
      print_product_id: printProduct?.id,
      print_product_size_id: printProductSize?.id,
    },
    skip: !printProduct?.id || !printProductSize?.id,
    fetchPolicy: 'cache-first',
  });
  const printProductSizeWithOrientations =
    printProductWithSizeInfo?.records?.[0]?.sizes?.records?.[0] ??
    printProductSize;
  const [configuring, setConfiguring] = useState(false);
  const openModal = useOpenModal();
  const classes = classNames({
    [className]: !!className,
    'purchasing-option-item': true,
    'purchasing-option-item-print-product': true,
  });

  const previewImage =
    printProductSize?.preview_asset_image?.url ??
    printProduct?.preview_asset_image?.url;

  const pricedTag = photo?.price_album_folder_tag;
  const pricedTagPrintProductItem = (
    pricedTag?.print_product_items?.records ?? []
  ).find(
    printProductItem =>
      printProductItem.print_product_size.id === printProductSize.id,
  );

  const photoPricingPrintProductPrice =
    pricedTagPrintProductItem?.price ?? photoPricingPrintProductItem?.price;
  return (
    <Fragment>
      <div
        className={classes}
        data-testid={`PrintProductIndividual-${photoPricingPrintProductItem.id}`}
      >
        <div>
          <div className="purchasing-option-item-info-extra">
            <Badge
              badgeStyle="yellow"
              className="purchasing-option-item-info-badge"
            >
              <PriceDisplay
                price={photoPricingPrintProductPrice?.value}
                currency={photoPricingPrintProductPrice?.currency}
                trimDecimalZeros={false}
              />
            </Badge>
          </div>

          <div className="purchasing-option-item-thumb">
            <img
              width={previewImage ? 80 : 60}
              src={previewImage || getAsset('/simple-prints-illustration.svg')}
              alt=""
            />
          </div>
        </div>

        <div className="purchasing-option-item-content-wrap">
          <div className="purchasing-option-item-title">
            {printProductSize?.print_size?.name}
          </div>
          <p className="mb-0 text-xs text-dark">
            {(printProduct?.name ?? '').replace(
              ' (configured as rendered sheets)',
              '',
            )}
          </p>

          {disabled && disabledElement && !readOnly && <>{disabledElement}</>}

          {!readOnly && (
            <CheckoutItemQuantityInput
              min={0}
              className="mt-1"
              disabled={loadingPrintProductSizeInfo}
              onChange={value => {
                if (disabled) {
                  openModal(REQUIRES_PRINT_PACKAGE_ALERT_MODAL, {
                    name: `${printProduct.name} - ${printProductSize.name}`,
                  });
                  return;
                }

                if (!cartItem) {
                  setConfiguring(true);
                  return;
                }

                if ((!cartItem && value > 0) || (!!cartItem && value === 0)) {
                  // Either no cart item in cart or removing cart item.
                  onCartItemToggle({
                    printProduct,
                    printProductSize,
                  });
                  return;
                }

                // Else, update the quantity.
                onCartItemUpdate(cartItem.id, {
                  quantity: value,
                });
              }}
              value={cartItem?.quantity || 0}
              label=""
            />
          )}
        </div>
      </div>
      {configuring && photo && printProduct?.card && (
        <PrintProductConfiguratorModal
          forceOpen
          printProductId={printProduct.id}
          printProductSizeId={printProductSize.id}
          availablePrintProductSizeIds={(printProduct.sizes?.records ?? []).map(
            ppz => ppz.id,
          )}
          controlsDisabled
          photo={photo}
          photoId={photo.id}
          orientation={getPreferredPrintProductSizeOrientation({
            printProductSize: printProductSizeWithOrientations,
            photo,
          })}
          onClose={() => setConfiguring(false)}
          onComplete={({
            accountCard,
            albumCustomPrint,
            printProduct: cardPrintProduct,
            printProductSize: cardPrintProductSize,
          }) => {
            onCartItemToggle({
              printProduct: cardPrintProduct ?? printProduct,
              printProductSize: cardPrintProductSize ?? printProductSize,
              accountCard,
              albumCustomPrint,
            });

            setConfiguring(false);
          }}
          albumMembershipGroup={albumMembershipGroup}
          identity={identity}
          printBackgrounds={printBackgrounds}
          watermarkSettings={watermarkSettings}
          isAllPhotos={isAllPhotos}
        />
      )}
      {configuring && photo && !printProduct?.card && (
        <PrintProductSizeRoesConfiguratorModal
          forceOpen
          printProduct={printProduct}
          printProductSize={printProductSize}
          photo={photo}
          identity={identity}
          albumMembershipGroup={albumMembershipGroup}
          isAllPhotos={isAllPhotos}
          onComplete={({
            printProduct: roesPrintProduct,
            printProductSize: roesPrintProductSize,
            roesConfiguration,
          }) => {
            onCartItemToggle({
              printProduct: roesPrintProduct ?? printProduct,
              printProductSize: roesPrintProductSize ?? printProductSize,
              roesConfiguration,
            });

            setConfiguring(false);
          }}
          onClose={() => setConfiguring(false)}
        />
      )}
    </Fragment>
  );
};

PrintProductIndividual.propTypes = propTypes;

export default PrintProductIndividual;
